import type {
  AggregatedOrderView,
  QuantityBreakdownFields,
} from '@/types/api/aggregated/order'
import type { AggregatedRfqView } from '@/types/api/aggregated/rfq'
import type { Amount, ExecutionOrder } from '@/types/api/order'

import { createLogger } from '@/lib/logger'
import { OrderUpdateEventType } from '@/types/api/events/order-update'
import {
  RFQEventType,
  type RFQUpdateEventMessage,
} from '@/types/api/events/rfq-update'
import { ExecutionOrderProtocol, ExecutionOrderStatus } from '@/types/api/order'
import { RFQStatus } from '@/types/api/rfq'
import { AdvisorViewStatus } from '@/types/enums/advisor-view-status'
import { OrderStatus } from '@/types/enums/order'
import { TraderViewStatus } from '@/types/enums/trader-view-status'

export enum UserArchetype {
  Trader = 'trader',
  Advisor = 'advisor',
}

export type TradeBlotterOrderStatus =
  | TraderViewStatus
  | AdvisorViewStatus
  | 'unknown'

const LOGGER = createLogger('utils/trade-blotter/compute-order-status')

interface DueTimeStatus {
  currentTimeISO: string
  currentTimePlusOneSecondISO: string
  isDueTimeReached: boolean
}

/**
 * Checks if a due time has been reached with a 1-second buffer to account for potential clock skew.
 *
 * This function generates current time and current time plus one second timestamps, then compares
 * the due time against the buffered time. The 1-second buffer prevents RFQs from transitioning
 * to "response required" status prematurely due to slight timing differences between systems.
 *
 * @param dueTime - The due time to check, can be a timestamp string, null, or undefined
 * @returns An object containing:
 *   - currentTime: Current time in ISO string format
 *   - currentTimePlusOneSecond: Current time plus 1 second in ISO string format
 *   - isDueTimeReached: Boolean indicating if due time has been reached (with buffer)
 */
export function checkIfDueTimeReachedWithBuffer(
  dueTime: string | null | undefined,
): DueTimeStatus {
  const nowMS = Date.now()
  const currentTimeISO = new Date(nowMS).toISOString()
  const currentTimePlusOneSecondISO = new Date(nowMS + 1000).toISOString()
  const isDueTimeReached =
    !!dueTime && new Date(dueTime) <= new Date(currentTimePlusOneSecondISO)

  return {
    currentTimeISO,
    currentTimePlusOneSecondISO,
    isDueTimeReached,
  }
}

/**
 * Checks whether a due time event has been received for an RFQ.
 *
 * This function iterates over the RFQ events and checks if any of them have
 * the event type RFQEventType.DueTime.
 *
 * @param events - The events array from an RFQ, can be null, undefined, or an empty array
 * @returns Boolean indicating if a due time event has been received
 */
export function hasDueTimeEventBeenReceived(
  events: RFQUpdateEventMessage[] | null | undefined,
): boolean {
  if (!events || events.length === 0) {
    return false
  }

  return events.some(
    (event) => event?.event?.event_type === RFQEventType.DueTime,
  )
}

function computeOrderStatusForOrderPendingExecutionOrPartiallyFilled(
  aggregatedOrderView: AggregatedOrderView,
  aggregatedRfqViews: AggregatedRfqView[] | null,
): TradeBlotterOrderStatus {
  // PartiallyRouted fires whenever the parent has had any routing activity
  // (active or terminal EOs) AND unallocated quantity remains. Asset-class
  // agnostic — the derivation operates on raw quantity values from the
  // breakdown. The two extremes fall through to the existing logic below:
  // fresh orders (remaining === full amount) into the RFQ-aware path, and
  // fully-routed orders (remaining === 0) into the pending-EO protocol
  // switch.
  const breakdown = computeOrderQuantityBreakdown(aggregatedOrderView)
  const totalAmount = aggregatedOrderView.amount?.value ?? 0
  if (breakdown.remaining > 0 && breakdown.remaining < totalAmount) {
    LOGGER.info(
      `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.PartiallyRouted} - parent has routed quantity but unallocated quantity remains`,
      {
        parent_order_id: aggregatedOrderView.id,
        filled: breakdown.filled,
        submitted: breakdown.submitted,
        remaining: breakdown.remaining,
        total_amount: totalAmount,
      },
    )
    return TraderViewStatus.PartiallyRouted
  }

  const pendingExecutionOrders =
    aggregatedOrderView?.execution_orders?.filter(
      (executionOrder) =>
        executionOrder.status === ExecutionOrderStatus.PendingExecution ||
        // TODO: Check on this later, not 100% certain we should treat partial fills the same here
        executionOrder.status === ExecutionOrderStatus.PartiallyFilled,
    ) ?? []
  if (pendingExecutionOrders.length === 0) {
    // Defensive: aggregatedRfqViews may contain undefined elements at runtime
    const validRfqViews = aggregatedRfqViews?.filter((v) => v != null) ?? null
    const latestRfq = validRfqViews?.sort(
      (a, b) =>
        new Date(b.latest_state.created_at ?? '').getTime() -
        new Date(a.latest_state.created_at ?? '').getTime(),
    )[0]
    const latestRfqEvent = latestRfq?.events?.[latestRfq?.events.length - 1]
    const latestRfqStatus = latestRfq?.latest_state.status

    // Detect RFQs referenced on the order that don't have view data yet.
    // This replaces a fragile heuristic that checked if the last order event
    // was "rfq_create" — that broke when "order_metadata_update" displaced it.
    const rfqIdsOnOrder = new Set(
      aggregatedOrderView.rfqs?.map((r) => r.id) ?? [],
    )
    const rfqIdsWithViews = new Set(validRfqViews?.map((v) => v.id) ?? [])
    const hasUnresolvedRfq = [...rfqIdsOnOrder].some(
      (id) => !rfqIdsWithViews.has(id),
    )

    const isLatestRfqActive =
      latestRfqStatus === RFQStatus.Open || hasUnresolvedRfq

    // Add logging to check to get visibility into if we are properly setting the trader view status for RFQs transitioning between due and firm time
    const dueTime = latestRfq?.latest_state.due_time
    const { currentTimeISO, currentTimePlusOneSecondISO, isDueTimeReached } =
      checkIfDueTimeReachedWithBuffer(dueTime)

    const hasDueTimeEventBeenReceivedForRfq = hasDueTimeEventBeenReceived(
      latestRfq?.events,
    )

    const logMetadata = {
      parent_order_id: aggregatedOrderView.id,
      orderId: aggregatedOrderView.id,
      rfqIdsOnOrder: [...rfqIdsOnOrder],
      rfqIdsWithViews: [...rfqIdsWithViews],
      hasUnresolvedRfq,
      latest_rfq: latestRfq,
      latest_rfq_event: latestRfqEvent,
      has_due_time_event_been_received: hasDueTimeEventBeenReceivedForRfq,
      latest_rfq_active: isLatestRfqActive,
      latest_rfq_status: latestRfqStatus,
      due_time: dueTime,
      current_time: currentTimeISO,
      current_time_plus_one_second: currentTimePlusOneSecondISO,
      due_time_reached: isDueTimeReached,
    }
    if (isLatestRfqActive) {
      // We check isDueTimeReached and hasDueTimeEventBeenReceivedForRfq separately to provide
      // detailed logging for debugging and monitoring purposes. This helps us understand the
      // exact state when transitioning to RFQResponseRequired status:
      // - Whether the due time has actually passed (clock-based)
      // - Whether we've received the due time event from the system (event-based)
      //
      // This separation is important because:
      // 1. Clock skew between systems might cause due time to be reached before/after the event
      // 2. Network delays might cause events to arrive before/after the actual due time
      // 3. Different business logic might depend on one vs the other
      // 4. Monitoring and alerting can be more granular with separate logging
      if (isDueTimeReached && hasDueTimeEventBeenReceivedForRfq) {
        LOGGER.info(
          `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.RFQResponseRequired} - active RFQ and due time reached and due time event received`,
          logMetadata,
        )
        return TraderViewStatus.RFQResponseRequired
      }
      if (isDueTimeReached && !hasDueTimeEventBeenReceivedForRfq) {
        LOGGER.info(
          `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.RFQResponseRequired} - active RFQ and due time reached but due time event not received`,
          logMetadata,
        )
        return TraderViewStatus.RFQResponseRequired
      }

      if (!isDueTimeReached && hasDueTimeEventBeenReceivedForRfq) {
        LOGGER.info(
          `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.RFQResponseRequired} - active RFQ and due time event received, but due time not reached`,
          logMetadata,
        )
        return TraderViewStatus.RFQResponseRequired
      }
      LOGGER.info(
        `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.PendingRFQ} - active RFQ and due time not reached, nor due time event received`,
        logMetadata,
      )
      return TraderViewStatus.PendingRFQ
    }
    const isLatestRfqPending =
      latestRfqStatus !== undefined &&
      !isRfqInTerminalState(latestRfqStatus as RFQStatus)
    if (isLatestRfqPending) {
      LOGGER.info(
        `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.PendingRFQ} - latest RFQ is pending`,
        logMetadata,
      )
      return TraderViewStatus.PendingRFQ
    }
    LOGGER.info(
      `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.RoutingRequired} - latest RFQ is not active or pending`,
      logMetadata,
    )
    return TraderViewStatus.RoutingRequired
  } else {
    const pendingExternalExecutionOrderProtocol =
      pendingExecutionOrders.length > 0
        ? pendingExecutionOrders[0]?.protocol
        : undefined

    const logMetadata = {
      parent_order_id: aggregatedOrderView.id,
      pending_external_execution_order_protocol:
        pendingExternalExecutionOrderProtocol,
    }
    switch (pendingExternalExecutionOrderProtocol) {
      case ExecutionOrderProtocol.External:
        LOGGER.info(
          `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.PendingExecution} - pending execution order protocol is external`,
          logMetadata,
        )
        return TraderViewStatus.PendingExecution
      // execution_order.protocol is group when the order was part of a GPO
      // and has been ungrouped
      case ExecutionOrderProtocol.Group:
        LOGGER.info(
          `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.RoutingRequired} - pending execution order protocol is group`,
          logMetadata,
        )
        return TraderViewStatus.RoutingRequired
      default:
        LOGGER.info(
          `Setting order ${aggregatedOrderView.id} to ${TraderViewStatus.PendingExecution} - pending execution order protocol is not external or group`,
          logMetadata,
        )
        return TraderViewStatus.PendingExecution
    }
  }
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function computeOrderStatus(
  aggregatedOrderView: AggregatedOrderView,
  aggregatedRfqViews: AggregatedRfqView[] | null,
  userArchetype: UserArchetype,
): TradeBlotterOrderStatus {
  if (userArchetype === UserArchetype.Trader) {
    switch (aggregatedOrderView.status) {
      case OrderStatus.Rejected:
        return TraderViewStatus.Rejected
      case OrderStatus.Canceled:
        return TraderViewStatus.Canceled
      case OrderStatus.Filled:
        return TraderViewStatus.Filled
      case OrderStatus.PendingReview:
        // `pending_review` (BE feature-flagged) is the explicit form of the
        // FlaggedForReview derivation below. Both paths coexist: flag off → the
        // BE sends pending_approval + FlaggedForReview; flag on → pending_review.
        return TraderViewStatus.ApprovalRequired
      case OrderStatus.PendingApproval:
        if (
          aggregatedOrderView.last_order_update_event?.event_type ===
          OrderUpdateEventType.FlaggedForReview
        ) {
          return TraderViewStatus.ApprovalRequired
        }
        return TraderViewStatus.PendingApproval
      case OrderStatus.PendingExecution:
      case OrderStatus.PartiallyFilled:
        return computeOrderStatusForOrderPendingExecutionOrPartiallyFilled(
          aggregatedOrderView,
          aggregatedRfqViews,
        )
      default:
        return 'unknown'
    }
  } else {
    // Advisor
    switch (aggregatedOrderView.status) {
      // Advisors have no ApprovalRequired concept; `pending_review` reads the
      // same as `pending_approval` here, mirroring the trader-side flag fallback.
      case OrderStatus.PendingReview:
      case OrderStatus.PendingApproval:
        return AdvisorViewStatus.PendingApproval
      case OrderStatus.PendingExecution:
        return AdvisorViewStatus.PendingExecution
      case OrderStatus.PartiallyFilled:
        return AdvisorViewStatus.PartiallyFilled
      case OrderStatus.Rejected:
        return AdvisorViewStatus.Rejected
      case OrderStatus.Canceled:
        return AdvisorViewStatus.Canceled
      case OrderStatus.Filled:
        return AdvisorViewStatus.Filled
      default:
        return 'unknown'
    }
  }
}

export function isTerminalStatus(status: TradeBlotterOrderStatus): boolean {
  return (
    // Trader statuses
    status === TraderViewStatus.Rejected ||
    status === TraderViewStatus.Canceled ||
    status === TraderViewStatus.Filled ||
    // Advisor statuses
    status === AdvisorViewStatus.Rejected ||
    status === AdvisorViewStatus.Canceled ||
    status === AdvisorViewStatus.Filled
  )
}

export function isActionRequired(status: TradeBlotterOrderStatus): boolean {
  return (
    status === TraderViewStatus.ApprovalRequired ||
    status === TraderViewStatus.RoutingRequired ||
    status === TraderViewStatus.RFQResponseRequired ||
    status === TraderViewStatus.PartiallyRouted
  )
}

export function isActiveStatus(status: TradeBlotterOrderStatus): boolean {
  return (
    // Trader statuses
    status === TraderViewStatus.PendingApproval ||
    status === TraderViewStatus.PendingExecution ||
    status === TraderViewStatus.PendingRFQ ||
    status === TraderViewStatus.PartiallyRouted ||
    // Advisor statuses
    status === AdvisorViewStatus.PendingApproval ||
    status === AdvisorViewStatus.PendingExecution ||
    status === AdvisorViewStatus.PartiallyFilled
  )
}

export function isRfqInTerminalState(status: RFQStatus): boolean {
  return (
    status === RFQStatus.Rejected ||
    status === RFQStatus.Expired ||
    status === RFQStatus.Canceled ||
    status === RFQStatus.Filled
  )
}

/**
 * Three-component decomposition of a parent order's total quantity.
 * Produced by {@link computeOrderQuantityBreakdown}.
 *
 * @public — first consumer lands in TR-2680 (equities routing surface).
 */
export interface OrderQuantityBreakdown {
  filled: number
  submitted: number
  remaining: number
}

const ACTIVE_EXECUTION_ORDER_STATUSES: ReadonlySet<ExecutionOrderStatus> =
  new Set([
    ExecutionOrderStatus.PendingExecution,
    ExecutionOrderStatus.PartiallyFilled,
  ])

/**
 * Decomposes a parent order's total quantity into three components:
 *
 * - `filled`: Σ `filled_amount.value` across all execution orders (active and
 *   terminal). Includes partial fills on active EOs and the filled portion of
 *   canceled EOs. Replaced EOs are excluded: their fills carry over into the
 *   successor's `filled_amount`, so counting both would double-count.
 * - `submitted`: Σ (`amount.value` − `filled_amount.value`) across active
 *   execution orders only. The still-pending reservation in open EOs.
 * - `remaining`: `parent.amount.value` − `filled` − `submitted`. What is still
 *   unallocated and can be staged into new execution orders.
 *
 * Invariant: `filled + submitted + remaining === parent.amount.value`.
 *
 * The math is asset-class agnostic — it operates on raw quantity values without
 * regard to whether `amount.type` is par, notional, or shares. Equity callers
 * use this to gate routing-surface visibility on `remaining > 0` (FR-014).
 */
export function computeOrderQuantityBreakdown(
  order: Pick<AggregatedOrderView, 'amount' | 'execution_orders'>,
): OrderQuantityBreakdown {
  const totalAmount = order.amount?.value ?? 0
  const executionOrders = order.execution_orders ?? []

  let filled = 0
  let submitted = 0
  for (const executionOrder of executionOrders) {
    // A replaced EO's fills are inherited by its successor's `filled_amount`
    // (FIX cumQty carry-over), so counting both double-counts the overlap.
    if (executionOrder.status === ExecutionOrderStatus.Replaced) {
      continue
    }
    filled += executionOrder.filled_amount.value
    if (ACTIVE_EXECUTION_ORDER_STATUSES.has(executionOrder.status)) {
      submitted +=
        executionOrder.amount.value - executionOrder.filled_amount.value
    }
  }

  const remaining = totalAmount - filled - submitted
  return { filled, submitted, remaining }
}

/**
 * Maps {@link computeOrderQuantityBreakdown}'s raw numbers onto the
 * `Amount`-shaped fields stored on the decorated order, so every quantity on
 * the order (`amount`, `filled_amount`, and these) reads and formats the same
 * way. Takes only the two order fields the breakdown needs, in their raw types,
 * so it isn't coupled to the full order view. The unit is copied from
 * `amount.type`; when `amount` is absent (pre-hydration), all three are null.
 *
 * - `routedAmount` = filled + working (quantity sent to a venue)
 * - `workingAmount` = breakdown.submitted (live at a venue, not yet filled)
 * - `unroutedAmount` = breakdown.remaining (not yet routed)
 */
export function deriveQuantityBreakdownFields({
  amount,
  executionOrders,
}: {
  amount: Amount | undefined
  executionOrders: ExecutionOrder[] | undefined
}): QuantityBreakdownFields {
  const { filled, submitted, remaining } = computeOrderQuantityBreakdown({
    amount,
    execution_orders: executionOrders ?? [],
  })
  const amountType = amount?.type ?? null
  const toAmount = (value: number): Amount | null =>
    amountType === null ? null : { type: amountType, value }

  return {
    routedAmount: toAmount(filled + submitted),
    workingAmount: toAmount(submitted),
    unroutedAmount: toAmount(remaining),
  }
}
